// Redaction + validation for client error reports.
//
// Pure and dependency-free so it is (a) unit-testable in vitest and (b) safe to
// mirror into the Deno edge function. Strips anything that could leak credentials
// or PII from error text BEFORE it is stored — supabase-js error strings routinely
// embed the request URL with the `apikey` query param, JWTs, and user emails.

export const MAX_FIELD_LEN = 8_000;
export const MAX_PAYLOAD_BYTES = 64 * 1024;

export const ERROR_TYPES = ['crash', 'query', 'rpc', 'auth', 'diagnostic'] as const;
export type ErrorType = (typeof ERROR_TYPES)[number];

export interface ClientErrorReport {
  error_message: string;
  stack_trace?: string | null;
  page?: string | null;
  query_name?: string | null;
  http_status?: number | null;
  input_data?: unknown;
  error_type?: ErrorType;
  auth_diagnostics?: unknown;
  client_id?: string | null;
}

// Order matters: redact JWTs and key=value secrets before the broader patterns.
const REDACTIONS: [RegExp, string][] = [
  // JWTs (header.payload.signature) — access/refresh tokens, even embedded in a URL.
  [/eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}/g, '[REDACTED_JWT]'],
  // Secret-bearing query params / key=value pairs (apikey, access_token, refresh_token, token, key).
  [/\b(apikey|access_token|refresh_token|token|key)=([^&\s"'#]+)/gi, '$1=[REDACTED]'],
  // Bearer tokens in Authorization headers that leaked into a message.
  [/Bearer\s+[A-Za-z0-9._~+/-]+=*/gi, 'Bearer [REDACTED]'],
  // Emails.
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[REDACTED_EMAIL]'],
  // US SSNs.
  [/\b\d{3}-\d{2}-\d{4}\b/g, '[REDACTED_SSN]'],
];

export function redactString(s: string): string {
  let out = s;
  for (const [re, rep] of REDACTIONS) out = out.replace(re, rep);
  return out;
}

/** Deep-redact every string inside an arbitrary JSON value, capping string length. */
export function redactValue(v: unknown, depth = 0): unknown {
  if (depth > 6) return '[REDACTED_DEPTH]';
  if (typeof v === 'string') return redactString(v).slice(0, MAX_FIELD_LEN);
  if (Array.isArray(v)) return v.slice(0, 200).map((x) => redactValue(x, depth + 1));
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k] = redactValue(val, depth + 1);
    return out;
  }
  return v;
}

/** Validate and normalize an untrusted error report. Used at the edge-function trust boundary. */
export function validateClientErrorReport(
  raw: unknown,
): { ok: true; value: ClientErrorReport } | { ok: false; error: string } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ok: false, error: 'payload must be an object' };
  const r = raw as Record<string, unknown>;
  if (typeof r.error_message !== 'string' || r.error_message.trim().length === 0) {
    return { ok: false, error: 'error_message is required' };
  }
  const error_type = (typeof r.error_type === 'string' && (ERROR_TYPES as readonly string[]).includes(r.error_type))
    ? (r.error_type as ErrorType)
    : 'crash';
  const http_status = typeof r.http_status === 'number' && Number.isFinite(r.http_status)
    ? Math.trunc(r.http_status)
    : null;
  return {
    ok: true,
    value: {
      error_message: r.error_message,
      stack_trace: typeof r.stack_trace === 'string' ? r.stack_trace : null,
      page: typeof r.page === 'string' ? r.page : null,
      query_name: typeof r.query_name === 'string' ? r.query_name : null,
      http_status,
      input_data: r.input_data ?? null,
      error_type,
      auth_diagnostics: r.auth_diagnostics ?? null,
      client_id: typeof r.client_id === 'string' ? r.client_id.slice(0, 64) : null,
    },
  };
}

/** Apply redaction + length caps to a (validated) report, returning a storage-safe copy. */
export function sanitizeClientErrorReport(r: ClientErrorReport): ClientErrorReport {
  return {
    error_message: redactString(r.error_message).slice(0, MAX_FIELD_LEN),
    stack_trace: r.stack_trace ? redactString(r.stack_trace).slice(0, MAX_FIELD_LEN) : null,
    page: r.page ? redactString(r.page).slice(0, 500) : null,
    query_name: r.query_name ? redactString(r.query_name).slice(0, 200) : null,
    http_status: r.http_status ?? null,
    input_data: redactValue(r.input_data ?? null),
    error_type: r.error_type ?? 'crash',
    auth_diagnostics: redactValue(r.auth_diagnostics ?? null),
    client_id: r.client_id ?? null,
  };
}
